import { useParams, useNavigationType } from 'react-router-dom'
import { GenericWizard, WizardStep, useWizard } from '@/components/common/wizard/GenericWizard'

import UploadAssets from './screens/uploadAssets'
import SetMediaCrop from './screens/mediaCrop/Screen'
import setVideoThumbnail from './screens/setVideoThumbnail/Screen'
import { SetAdContent } from './screens/SetAdContent'
import { IdentityBranding } from './screens/IdentityBranding'
import { ReviewSave } from './screens/ReviewSave'
import { LeadForm } from './screens/LeadForm.jsx'
import { SelectFormat } from './screens/SelectFormat.jsx'
import { FormQuestions } from './screens/FormQuestions.jsx'
import { FormPrivacy } from './screens/FormPrivacy.jsx'
import { FormThankYou } from './screens/FormThankYou.jsx'
import { FormType } from './screens/FormType.jsx'
import { FormIntro } from './screens/FormIntro.jsx'
import VideoTrim from './screens/videoTrim/Screen'
import CarouselCards from './screens/carouselCards/Screen'
import AiAssetGeneration from './screens/aiAssetGeneration/textToImage/Screen'
import AiImageToImage from './screens/aiAssetGeneration/imageToImage/Screen'
import AiAssetLibrary from './screens/aiAssetGeneration/library/Screen'
import AiVideoGeneration from './screens/aiAssetGeneration/textToVideo/Screen'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import EmptyWizard from './EmptyWizard'
import { Modal, Button } from 'react-bootstrap'
import AdCreative from '@/components/common/icons/AdCreative'
import { useContext, useEffect, useMemo, useState, useRef } from 'react'
import { SettingsContext } from '@/context/settings-context'

const getCreativeDisplayName = (name, isClone = false) =>
    isClone ? `Copy of ${name || 'Untitled Creative'}` : name || ''

// Screen mappings for ad creative wizard
const adCreativeScreensMap = {
    uploadAssets: UploadAssets,
    setMediaCrop: SetMediaCrop,
    setVideoThumbnail: setVideoThumbnail,
    setAdContent: SetAdContent,
    identityBranding: IdentityBranding,
    reviewSave: ReviewSave,
    aiTextToImage: AiAssetGeneration,
    aiImageToImage: AiImageToImage,
    aiAssetLibrary: AiAssetLibrary,
    aiTextToVideo: AiVideoGeneration,
    leadForm: LeadForm,
    selectFormat: SelectFormat,
    formQuestions: FormQuestions,
    formPrivacy: FormPrivacy,
    formThankYou: FormThankYou,
    formType: FormType,
    formIntro: FormIntro,
    carouselCards: CarouselCards,
    videoTrim: VideoTrim,
}

// Unfinished Campaign Modal Component
const UnfinishedCampaignModal = ({ show, onClose, onContinue, onStartOver }) => {
    const { theme } = useContext(SettingsContext)
    const isLight = theme === 'light'

    return (
        <Modal show={show} onHide={onClose} centered backdrop="static" contentClassName="border rounded">
            <Modal.Header closeButton className="border-bottom p-3">
                <Modal.Title>Unfinished Creative</Modal.Title>
            </Modal.Header>

            <Modal.Body className="border-bottom p-0 pt-2">
                <div
                    className={`w-100 d-flex flex-column align-items-center px-4 py-5 ${
                        isLight ? 'text-dark bg-white' : 'text-white'
                    }`}
                >
                    <div
                        className="mx-auto d-flex align-items-center justify-content-center rounded-circle mb-4"
                        style={{
                            width: '80px',
                            height: '80px',
                            backgroundColor: 'rgba(75, 85, 99, 0.8)', // Gray background for the icon
                        }}
                    >
                        <AdCreative />
                    </div>

                    <div className="mb-2 pb-0 text-center" style={{ maxWidth: 355, width: '100%', fontSize: '14px' }}>
                        It looks like you have an unfinished ad creative. Would you like to continue editing it or start
                        a new one from scratch?
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-center gap-3 px-4 pb-4">
                <Button
                    variant="outline-primary"
                    size="sm"
                    className="media-buying-button-action"
                    style={{
                        minWidth: '160px',
                        fontSize: '14px',
                    }}
                    onClick={onStartOver}
                >
                    Start New
                </Button>

                <Button
                    variant="primary"
                    size="sm"
                    style={{
                        minWidth: '160px',
                        fontSize: '14px',
                    }}
                    onClick={onContinue}
                >
                    Continue Draft
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

// Navigation Detection Component
const NavigationDetector = ({ campaignId, children }) => {
    const navigationType = useNavigationType()
    const [showDialog, setShowDialog] = useState(false)
    const hasCheckedNavigation = useRef(false)
    const [wizardReset, setWizardReset] = useState(false)

    useEffect(() => {
        // Only check once when component mounts
        if (hasCheckedNavigation.current) return
        hasCheckedNavigation.current = true

        const storageKey = `adCreativeWizardState-${campaignId}`
        const savedState = localStorage.getItem(storageKey)

        if (savedState) {
            try {
                const state = JSON.parse(savedState)

                // Check if wizard is not on first step (selectFormat)
                const isNotFirstStep = state.currentStepName && state.currentStepName !== 'selectFormat'

                if (isNotFirstStep) {
                    // Use React Router's navigation type to determine how user arrived
                    // PUSH = navigated from another page (show dialog)
                    // POP = refresh, back/forward, or direct URL (don't show dialog)
                    // REPLACE = location was replaced (don't show dialog)

                    if (navigationType === 'PUSH') {
                        setShowDialog(true)
                    }
                }
            } catch (error) {
                console.warn('Failed to parse wizard state:', error)
            }
        }
    }, [campaignId])

    const handleContinue = () => {
        setShowDialog(false)
    }

    const handleStartOver = () => {
        // Clear localStorage manually since we're outside WizardProvider
        const storageKey = `adCreativeWizardState-${campaignId}`
        localStorage.removeItem(storageKey)
        setShowDialog(false)
        // Trigger a reset by changing a key that will remount the GenericWizard
        setWizardReset((prev) => !prev)
    }

    return (
        <>
            <UnfinishedCampaignModal
                show={showDialog}
                onClose={() => setShowDialog(false)}
                onContinue={handleContinue}
                onStartOver={handleStartOver}
            />
            <div key={wizardReset}>{children}</div>
        </>
    )
}

const normalizeFormat = (format) => {
    const f = (format || '').toLowerCase()
    if (f === 'image' || f === 'singleimage' || f === 'single_image') return 'singleImage'
    if (f === 'carousel') return 'carousel'
    if (f === 'video') return 'video'
    return 'singleImage'
}

const parseJsonField = (value) => {
    if (!value) return []
    try {
        return typeof value === 'string' ? JSON.parse(value) : value || []
    } catch {
        return []
    }
}

const buildInitialContentCache = (creative, options = {}) => {
    if (!creative) return null

    const format = normalizeFormat(creative.format)
    const primaryTexts = parseJsonField(creative.primary_texts)
    const headlines = parseJsonField(creative.headlines)
    const descriptions = parseJsonField(creative.descriptions)
    const tags = parseJsonField(creative.tags)
    const firstAsset = creative.adAssets?.[0]
    const name = getCreativeDisplayName(creative.name, options.cloneName)

    const cache = {
        selectFormat: {
            format,
            showLeadForm: !!creative.lead_form_id,
        },
        setAdContent: {
            primaryText: primaryTexts[0] || '',
            headline: headlines[0] || '',
            description: descriptions[0] || '',
            cta: creative.call_to_action_type || 'Learn More',
            destinationUrl: creative.call_to_action_url || '',
            displayLink: creative.call_to_action_text || '',
        },
        uploadAssets: {
            source: firstAsset?.source === 'meta' || firstAsset?.source_id ? 'mediaLibrary' : 'upload',
        },
        reviewSave: {
            name,
            tags,
        },
    }

    if (creative.lead_form_id) {
        cache.leadForm = {
            meta_form_id: creative.lead_form_id,
            selectedForm: { id: creative.lead_form_id, meta_form_id: creative.lead_form_id },
        }
    }

    // Set assetId for edit and clone - clone uses source creative's assets for display and backend copy
    if (format !== 'carousel' && firstAsset) {
        cache.setMediaCrop = { assetId: firstAsset.id }
    }

    return cache
}

// Main Ad Creative Wizard Component
export const AdCreativeWizard = ({ editCreativeId, editCreativeData, cloneCreativeData } = {}) => {
    const { id: campaignId } = useParams()
    const isEditMode = !!editCreativeId
    const isCloneMode = !!cloneCreativeData && !editCreativeId

    const { connectedPublishers, refetch: refetchMediaBuying } = useMediaBuyingContext()
    const publisher = connectedPublishers ? Object.values(connectedPublishers)[0] : null

    useEffect(() => {
        if (!publisher) return
        if (publisher.page_id || !publisher.pixel) refetchMediaBuying()
    }, [publisher?.page_id, publisher?.pixel, refetchMediaBuying])

    const initialContentCache = useMemo(() => {
        if (isEditMode) return buildInitialContentCache(editCreativeData)
        if (isCloneMode) return buildInitialContentCache(cloneCreativeData, { cloneName: true })
        return null
    }, [editCreativeData, cloneCreativeData, isEditMode, isCloneMode])

    const editFormat = isEditMode ? normalizeFormat(editCreativeData?.format) : null
    const cloneFormat = isCloneMode ? normalizeFormat(cloneCreativeData?.format) : null
    const campaignFormat = editFormat || cloneFormat || 'singleImage'
    const hasLeadForm = !!(isEditMode
        ? editCreativeData?.lead_form_id
        : isCloneMode
          ? cloneCreativeData?.lead_form_id
          : false)

    const storageKey = isEditMode
        ? 'adCreativeEditState'
        : isCloneMode
          ? 'adCreativeCloneState'
          : 'adCreativeWizardState'
    const idExtractorFn = isEditMode
        ? () => `${campaignId}-${editCreativeId}`
        : isCloneMode
          ? () => `${campaignId}-clone-${cloneCreativeData.id}`
          : () => campaignId

    const wizardTitle = isEditMode ? 'Edit Ad' : isCloneMode ? 'Clone Ad' : 'New Ad'

    const wizardContent = (
        <GenericWizard
            title={wizardTitle}
            storageKey={storageKey}
            initialState={{ campaignFormat }}
            idExtractor={idExtractorFn}
            screensMap={adCreativeScreensMap}
            showStepper={false}
            existingId={isEditMode ? editCreativeId : undefined}
            initialContentCache={initialContentCache}
            editCreativeId={isEditMode ? editCreativeId : undefined}
            startAtReviewSave={isCloneMode}
            startAtStep={
                isEditMode ? 'selectFormat' : isCloneMode ? 'selectFormat' : undefined
            }
            cloneCreativeId={isCloneMode ? cloneCreativeData.id : undefined}
            campaignId={campaignId}
        >
            <WizardStep
                name="selectFormat"
                label="Choose Ad Format"
                className="tw:ml-3"
                visible={true}
            />
            <WizardStep
                name="leadForm"
                label="Attach Instant Form"
                visible={hasLeadForm}
                className="tw:ml-3"
            >
                <WizardStep name="formType" label="Form Type" className="tw:ml-6" />
                <WizardStep name="formIntro" label="Intro" className="tw:ml-6" />
                <WizardStep name="formQuestions" label="Custom Questions" className="tw:ml-6" />
                <WizardStep name="formPrivacy" label="Privacy Policy" className="tw:ml-6" />
                <WizardStep name="formThankYou" label="Thank You Screen" className="tw:ml-6" />
            </WizardStep>
            <WizardStep name="uploadAssets" label="Upload Creative Assets" className="tw:ml-3" />
            <WizardStep name="aiTextToImage" visible={false} label="Generate Assets" className="tw:ml-6" />
            <WizardStep name="aiAssetLibrary" visible={false} label="Generate Assets" className="tw:ml-6" />
            <WizardStep name="aiImageToImage" visible={false} label="Generate Assets" className="tw:ml-6" />
            <WizardStep name="aiTextToVideo" visible={false} label="Generate Video" className="tw:ml-6" />
            {/* Edit/clone start with a known format, so these must match SelectFormat's handleFormatChange up front */}
            <WizardStep
                name="carouselCards"
                visible={campaignFormat === 'carousel'}
                label="Carousel Cards"
                className="tw:ml-3"
            />
            <WizardStep name="videoTrim" visible={campaignFormat === 'video'} label="Trim Video" className="tw:ml-3" />
            <WizardStep
                name="setMediaCrop"
                visible={campaignFormat !== 'carousel'}
                label="Set Media Crop For Placement"
                className="tw:ml-3"
            />
            <WizardStep name="setAdContent" label="Add Primary Text And Creative Elements" className="tw:ml-3" />
            <WizardStep name="reviewSave" label="Review & Publish" className="tw:ml-3" />
        </GenericWizard>
    )

    if (!publisher || !publisher.page_id || !publisher.pixel) return <EmptyWizard />

    if (isEditMode || isCloneMode) return wizardContent

    return <NavigationDetector campaignId={campaignId}>{wizardContent}</NavigationDetector>
}

// Export useWizard for backward compatibility with existing screens
export { useWizard }
